<script setup lang="ts">
import { RotateCcw } from '@lucide/vue'
import {
    Button,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    ZhEmptyState,
    ZhFieldError,
    ZhFormField,
    ZhFormLayout,
    ZhPageTitle,
    ZhPanel,
    ZhSavePreview,
    ZhTooltip,
} from '@zerohuman/ui'
import { computed, onMounted, ref, watch } from 'vue'
import {
    api,
    canonicalModelTier,
    formatUsdCents,
    type Me,
    type Member,
    modelTierLabel,
    type Role,
    type SpendSnapshot,
    type TaskRow,
    type Team,
} from '@/api'
import CapLimitFields, { type CapDraft } from '@/components/CapLimitFields.vue'
import ListSearchField from '@/components/ListSearchField.vue'
import { useFieldValidation } from '@/composables/useFieldValidation'
import { useFormDraft } from '@/composables/useFormDraft'
import { useListState } from '@/composables/useListState'
import { buildSpendCapPreview } from '@/lib/formPreview'
import { memberLabel } from '@/lib/memberLabel'
import { SUGGESTED_DAY_CAP_CENTS } from '@/lib/spendSuggestion'

const me = ref<Me | null>(null)
const teams = ref<Team[]>([])
const members = ref<Member[]>([])
const roles = ref<Role[]>([])
const tasks = ref<TaskRow[]>([])
const snap = ref<SpendSnapshot | null>(null)
const error = ref<string | null>(null)
const saved = ref<string | null>(null)
const toggling = ref(false)
const drafts = ref<Record<string, CapDraft>>({})
const add = ref<CapDraft>(emptyDraft('enterprise', ''))

/** The suggested day cap as the add form's Day $ field shows it. */
const suggestedDayField = (SUGGESTED_DAY_CAP_CENTS / 100).toFixed(2)
/** The suggested day cap as the copy says it. Same constant as the field, so the two never drift. */
const suggestedDayCap = formatUsdCents(SUGGESTED_DAY_CAP_CENTS)

/**
 * True while the add form's Day $ holds the suggestion this page put there and nobody has changed it. The
 * suggestion is not the founder's draft, so the saved draft keeps the field blank: a later visit with caps
 * then shows a blank form, and a visit with none suggests again.
 */
const daySuggested = ref(false)

const addDraftBox = computed<CapDraft>({
    get: () => (daySuggested.value ? { ...add.value, moneyDay: '' } : add.value),
    set: value => {
        add.value = value
    },
})
const addDraft = useFormDraft('spend-add', { add: addDraftBox })
useFormDraft('spend-caps', { drafts })

watch(
    () => add.value.moneyDay,
    moneyDay => {
        if (moneyDay !== suggestedDayField) {
            daySuggested.value = false
        }
    },
)

/**
 * With no caps yet, carry the suggested enterprise day cap into the add form, so saving it unchanged applies
 * the suggestion. Never over a Day $ the founder typed or a saved draft restored.
 */
function suggestDayCap(): void {
    if (!snap.value || snap.value.caps.length > 0) {
        return
    }
    if (add.value.layer !== 'enterprise' || fieldText(add.value.moneyDay) !== '') {
        return
    }
    add.value.moneyDay = suggestedDayField
    daySuggested.value = true
}

type SpendCap = SpendSnapshot['caps'][number]

function emptyDraft(layer: string, layerId: string): CapDraft {
    return {
        layer,
        layerId,
        moneyDay: '',
        moneyMonth: '',
        moneyRun: '',
        runCountDay: '',
        maxModelTier: 'mid',
    }
}

function centsField(cents: number | null | undefined): string {
    return cents === null || cents === undefined ? '' : (cents / 100).toFixed(2)
}

function fieldText(raw: unknown): string {
    if (raw === null || raw === undefined) {
        return ''
    }
    return String(raw).trim()
}

function toCents(raw: unknown): number | null {
    const text = fieldText(raw)
    if (text === '') {
        return null
    }
    const value = Number(text)
    return Number.isNaN(value) ? null : Math.round(value * 100)
}

function toCount(raw: unknown): number | null {
    const text = fieldText(raw)
    if (text === '') {
        return null
    }
    const value = Number(text)
    return Number.isNaN(value) ? null : Math.trunc(value)
}

function capLabel(layer: string, layerId: string): string {
    if (layer === 'enterprise') {
        return me.value?.enterprise.name ?? 'Enterprise'
    }
    if (layer === 'team') {
        return teams.value.find(team => team.id === layerId)?.name ?? layerId
    }
    if (layer === 'member') {
        return members.value.find(member => member.id === layerId)?.name ?? layerId
    }
    if (layer === 'role') {
        return roles.value.find(role => role.id === layerId)?.name ?? layerId
    }
    return tasks.value.find(task => task.id === layerId)?.slug ?? layerId
}

/**
 * Today's spend as a share of one row's own day cap. Spend is only attributed enterprise-wide
 * (`spend_ledger` rows carry no team / member / role / task), so every row reads the same money
 * today against its own limit. `null` only when the row has no day cap at all: there is no share
 * to take. A cap explicitly set to $0 is a real "spend nothing today" limit, not an absent one, so
 * any money spent today already uses the whole of it.
 */
function dayUsedRatio(cap: SpendCap): number | null {
    const limit = cap.moneyCentsDay
    if (limit === null) {
        return null
    }
    const spent = snap.value?.spentTodayCents ?? 0
    if (limit <= 0) {
        return spent > 0 ? 1 : 0
    }
    return spent / limit
}

/** That share as a whole percent, for the row's used/limit cell. */
function dayUsedPercent(cap: SpendCap): number | null {
    const ratio = dayUsedRatio(cap)
    return ratio === null ? null : Math.round(ratio * 100)
}

/** A row at or past 80% of its own day cap: the same threshold the page banner warns at. */
function nearDayCap(cap: SpendCap): boolean {
    const ratio = dayUsedRatio(cap)
    return ratio !== null && ratio >= 0.8
}

/**
 * What a row's today figure actually counts. Spend is only recorded enterprise-wide, so a team /
 * member / role / task row reads the whole enterprise's money today against its own day cap: say so
 * on the cell, or a near-cap flag reads as that layer alone having overspent.
 */
function todaySpendNote(cap: SpendCap): string {
    if (cap.layer === 'enterprise') {
        return "Today's enterprise-wide spend against this cap's day limit."
    }
    return `Spend is only recorded enterprise-wide, so this is the enterprise's spend today against this ${cap.layer} cap's day limit, not this ${cap.layer}'s own spend.`
}

/** The layers a cap can sit on, in the order the ladder nests them. Drives the Layer filter. */
const CAP_LAYERS = ['enterprise', 'team', 'member', 'role', 'task'] as const

const layerFilter = ref('')
const capSearch = ref('')

/**
 * "All layers" in the Layer filter. A kit Select item cannot hold an empty value, so the choice holds this
 * stand-in and the filter itself stays `''`, as it always has.
 */
const ALL_LAYERS = 'all'

const layerChoice = computed(() => layerFilter.value || ALL_LAYERS)

function chooseLayerFilter(value: unknown): void {
    const layer = value === null || value === undefined ? '' : String(value)
    layerFilter.value = layer === ALL_LAYERS ? '' : layer
}

/**
 * What a cap's row can be searched by. `capLabel` is the name the row shows, which for a task is its
 * slug and for everyone else is a display name — so the slug (or a member's address) goes in too, or
 * searching a team by the slug you know it as finds nothing.
 */
function capSlug(cap: SpendCap): string {
    if (cap.layer === 'team') {
        return teams.value.find(team => team.id === cap.layerId)?.slug ?? ''
    }
    if (cap.layer === 'member') {
        return members.value.find(member => member.id === cap.layerId)?.email ?? ''
    }
    if (cap.layer === 'role') {
        return roles.value.find(role => role.id === cap.layerId)?.slug ?? ''
    }
    if (cap.layer === 'task') {
        return tasks.value.find(task => task.id === cap.layerId)?.slug ?? ''
    }
    return me.value?.enterprise.slug ?? ''
}

/**
 * The caps the table shows. A tenant that caps per task can run to dozens of rows, and the page had no
 * way to reach one but scrolling. Filtering is client-side because the snapshot is already one payload.
 */
const visibleCaps = computed(() => {
    const caps = snap.value?.caps ?? []
    const needle = capSearch.value.trim().toLowerCase()
    return caps.filter(cap => {
        if (layerFilter.value && cap.layer !== layerFilter.value) {
            return false
        }
        if (!needle) {
            return true
        }
        return `${capLabel(cap.layer, cap.layerId)} ${capSlug(cap)}`.toLowerCase().includes(needle)
    })
})

/** Rows exist but none match: say that, rather than reusing the "No caps yet. Add one below." copy. */
const capsFiltered = computed(() => Boolean(layerFilter.value || capSearch.value.trim()))

/** #633: the caps table's layer filter and search come back on the next visit; a layer no longer offered is dropped. */
const capList = useListState('spend', {
    layerFilter: { ref: layerFilter, allowed: () => [...CAP_LAYERS] },
    capSearch: { ref: capSearch },
})

function clearCapFilters(): void {
    capList.clear()
}

const spendOn = computed(() => snap.value?.enabled !== false)

async function toggleSpend(): Promise<void> {
    if (!snap.value) {
        return
    }
    await setSpend(!snap.value.enabled)
}

/** Spend off's one action: turn it on. Applying the suggested cap stays an explicit save in the add form. */
async function turnOnSpend(): Promise<void> {
    await setSpend(true)
}

async function setSpend(enabled: boolean): Promise<void> {
    if (!snap.value || toggling.value) {
        return
    }
    toggling.value = true
    saved.value = null
    error.value = null
    try {
        snap.value = await api.setSpendEnabled(enabled)
        if (snap.value.enabled) {
            await load()
        }
    } catch (caught) {
        error.value =
            caught instanceof Error ? `Could not update spend (${caught.message}).` : 'Could not update spend.'
    } finally {
        toggling.value = false
    }
}

const addChoices = computed(() => {
    if (add.value.layer === 'team') {
        return teams.value.map(team => ({ id: team.id, label: team.name }))
    }
    if (add.value.layer === 'member') {
        return members.value.map(member => ({ id: member.id, label: memberLabel(member) }))
    }
    // A role or task is offered by the name the preview then says (#1756), never its slug unless it has no name.
    if (add.value.layer === 'role') {
        return roles.value.map(role => ({ id: role.id, label: role.name || role.slug }))
    }
    if (add.value.layer === 'task') {
        return tasks.value.map(task => ({ id: task.id, label: task.name || task.slug }))
    }
    return []
})

/**
 * The add form's target is `required` (its Select's own rule, which the hidden native select holds when the form is
 * sent): a cap on a team, member, role or task names which one. Checked while choosing, and by Save (#1757).
 */
const addFields = useFieldValidation(
    computed(() => ({ layerId: add.value.layerId })),
    {
        layerId: value =>
            add.value.layer !== 'enterprise' && String(value ?? '').trim() === ''
                ? {
                      message: `No ${add.value.layer} is chosen.`,
                      fix: addChoices.value.length
                          ? `Pick the ${add.value.layer} this cap is for.`
                          : `There is no ${add.value.layer} to pick yet: add one, or put the cap on another layer.`,
                  }
                : null,
    },
    { idPrefix: 'cap-add' },
)

onMounted(async () => {
    try {
        me.value = await api.me()
        const [teamRows, memberRows, roleRows, taskRows] = await Promise.all([
            api.teams(),
            api.members(),
            api.roles(),
            api.tasks({ limit: 200 }),
        ])
        teams.value = teamRows
        members.value = memberRows
        roles.value = roleRows
        tasks.value = taskRows.data
        await load()
        if (me.value && add.value.layer === 'enterprise') {
            add.value.layerId = me.value.enterprise.id
        }
    } catch {
        error.value = 'Could not load spend.'
    }
})

async function load(preferServer?: { layer: string; layerId: string }): Promise<void> {
    snap.value = await api.spend()
    const next: Record<string, CapDraft> = {}
    for (const cap of snap.value.caps) {
        const fromServer: CapDraft = {
            layer: cap.layer,
            layerId: cap.layerId,
            moneyDay: centsField(cap.moneyCentsDay),
            moneyMonth: centsField(cap.moneyCentsMonth),
            moneyRun: centsField(cap.moneyCentsRun),
            runCountDay: cap.runCountDay === null ? '' : String(cap.runCountDay),
            // A cap row written before the #664 rename says `fast`; the picker only knows `economical`.
            maxModelTier: canonicalModelTier(cap.maxModelTier) ?? 'mid',
        }
        const existing = drafts.value[cap.id]
        const replaced = preferServer?.layer === cap.layer && preferServer?.layerId === cap.layerId
        next[cap.id] = existing && !replaced ? existing : fromServer
    }
    drafts.value = next
    suggestDayCap()
}

function capPatch(draft: CapDraft) {
    return {
        moneyCentsDay: toCents(draft.moneyDay),
        moneyCentsMonth: toCents(draft.moneyMonth),
        moneyCentsRun: toCents(draft.moneyRun),
        runCountDay: toCount(draft.runCountDay),
        maxModelTier: draft.maxModelTier || null,
    }
}

/** Each cap form's fields by form key (a cap id, or `add`), so Save checks them before it sends (#1757). */
type CapFieldsHandle = { check: () => unknown }
const capFields = new Map<string, CapFieldsHandle>()
function capFieldsRef(key: string): (instance: unknown) => void {
    return instance => {
        if (instance && typeof (instance as Partial<CapFieldsHandle>).check === 'function') {
            capFields.set(key, instance as CapFieldsHandle)
        } else {
            capFields.delete(key)
        }
    }
}

/** Shows every error a cap form has and says whether it has any (#1757). */
function formInvalid(formKey?: string): boolean {
    // Every error shows at once. The add form's target sits before its amounts, so it takes focus last, and wins.
    const capIssue = formKey === undefined ? null : (capFields.get(formKey)?.check() ?? null)
    const targetIssue = formKey === 'add' ? addFields.check() : null
    return capIssue !== null || targetIssue !== null
}

async function save(layer: string, layerId: string, draft: CapDraft, formKey?: string): Promise<void> {
    if (formInvalid(formKey)) {
        return
    }
    saved.value = null
    error.value = null
    try {
        const adding = draft === add.value
        const result = await api.patchSpendCap({ layer, layerId, ...capPatch(draft) })
        await load({ layer, layerId })
        if (adding) {
            add.value = emptyDraft('enterprise', me.value?.enterprise.id ?? '')
            addDraft.clear()
        }
        const message = result.raised
            ? 'Cap raised. Paused runs were re-queued and will re-check the cap.'
            : 'Cap saved.'
        // The add form's result shows under its own button, where the founder pressed Save; a row's stays on top.
        if (adding) addResult.value = message
        else saved.value = message
    } catch (caught) {
        error.value = caught instanceof Error ? `Could not save cap (${caught.message}).` : 'Could not save cap.'
    }
}

/** The name of what the add form's cap is for, as the founder knows it: never a slug or an id. */
function addTargetName(): string {
    const { layer, layerId } = add.value
    if (layer === 'enterprise') return me.value?.enterprise.name ?? ''
    if (layer === 'team') return teams.value.find(team => team.id === layerId)?.name ?? ''
    if (layer === 'member') {
        const member = members.value.find(row => row.id === layerId)
        return member ? memberLabel(member) : ''
    }
    if (layer === 'role') return roles.value.find(role => role.id === layerId)?.name ?? ''
    if (layer === 'task') return tasks.value.find(task => task.id === layerId)?.name ?? ''
    return ''
}

/**
 * The add form's save preview (#1756): Save says in plain words what the cap will hold, and nothing is sent until it
 * is confirmed. Any change to the form hides it as stale.
 */
const addPreview = ref<string[] | null>(null)
/** The confirmed add is being sent: the preview stays open with its confirm held, so it shows progress and cannot go twice. */
const addSaving = ref(false)
/** What the last confirmed add did ("Cap saved."), shown under the add form's own button. */
const addResult = ref<string | null>(null)

watch(
    add,
    () => {
        addPreview.value = null
    },
    { deep: true },
)

function openAddPreview(): void {
    // Validate first (CTO decision 19 on #1756): an invalid field shows its error, takes focus and opens no preview.
    if (formInvalid('add')) return
    const dollars = (raw: unknown) => {
        const cents = toCents(raw)
        return cents === null ? null : cents / 100
    }
    const { layer, layerId } = add.value
    addResult.value = null
    addPreview.value = buildSpendCapPreview({
        layerWord: layer,
        targetName: addTargetName(),
        dayUsd: dollars(add.value.moneyDay),
        monthUsd: dollars(add.value.moneyMonth),
        runUsd: dollars(add.value.moneyRun),
        runsPerDay: toCount(add.value.runCountDay),
        modelTier: add.value.maxModelTier ? modelTierLabel(add.value.maxModelTier) : null,
        replacesCap: (snap.value?.caps ?? []).some(cap => cap.layer === layer && cap.layerId === layerId),
    })
}

async function confirmAddPreview(): Promise<void> {
    if (addSaving.value) return
    addSaving.value = true
    try {
        // Confirm checks again before it sends (CTO decision 19 on #1756).
        await save(add.value.layer, add.value.layerId, add.value, 'add')
    } finally {
        addSaving.value = false
        addPreview.value = null
    }
}

/** The add form's Layer picker: take the layer, then repoint the cap at that layer's first target. */
function chooseAddLayer(value: unknown): void {
    add.value.layer = value === null || value === undefined ? '' : String(value)
    onLayerChange()
}

/** The add form's target picker: the id of the team, member, role or task the cap is for. */
function chooseAddTarget(value: unknown): void {
    add.value.layerId = value === null || value === undefined ? '' : String(value)
}

function onLayerChange(): void {
    if (add.value.layer === 'enterprise' && me.value) {
        add.value.layerId = me.value.enterprise.id
        return
    }
    add.value.layerId = addChoices.value[0]?.id ?? ''
}
</script>

<template>
	<section class="w-full space-y-4 p-8">
		<div class="flex flex-wrap items-center justify-between gap-4">
			<ZhPageTitle>Spend</ZhPageTitle>
			<!-- The words either side are part of the control: clicking Off or On flips the switch too. -->
			<label class="inline-flex shrink-0 items-center gap-2 text-sm">
				<span class="text-muted-foreground">Off</span>
				<Switch
					:model-value="spendOn"
					aria-label="Spend"
					:disabled="!snap || toggling"
					@update:model-value="toggleSpend"
				/>
				<span class="text-muted-foreground">On</span>
			</label>
		</div>
		<!-- #1762: one plain sentence under the title, with spend Off and On alike. A run at a day cap is parked as paused
			spend until the cap is raised, which is what "work pauses at the cap" says. -->
		<p class="text-muted-foreground text-sm">
			Set how much your team can spend per day. Work pauses at the cap until you raise it.
		</p>
		<template v-if="snap && spendOn">
		<p class="text-muted-foreground text-sm">
			Tightest cap wins (enterprise / team / member / role / task). Raising a day cap re-queues
			<code>paused_spend</code> runs.
			<template v-if="snap.capChangeGated"> Cap changes are a CEO action.</template>
		</p>
		</template>
		<!-- Spend off: say what the switch does, with the one action that turns it on. Caps survive the switch, so the
			starting cap is suggested only to an enterprise that has none, as the add form's prefill is. -->
		<ZhEmptyState v-else-if="snap" data-testid="spend-off">
			Spend is off, so no spend cap is enforced. Turn it on and every run checks the tightest cap across
			enterprise, team, member, role and task: a run that would pass it pauses until you raise the cap.
			Plan limits apply either way.<template v-if="!snap.caps.length"> A {{ suggestedDayCap }} day cap for the enterprise is a good place to start.</template>
			<template #action>
				<Button type="button" size="sm" :disabled="toggling" @click="turnOnSpend">Turn on Spend</Button>
			</template>
		</ZhEmptyState>
		<!-- Severity is the kit panel's tone, never a colour the screen names. -->
		<ZhPanel v-if="error" as="div" tone="failed">
			<p class="text-sm">{{ error }}</p>
		</ZhPanel>
		<template v-if="snap && spendOn">
		<p v-if="saved" class="text-sm">{{ saved }}</p>
		<ZhPanel v-if="snap.warning80" as="div" tone="action">
			<p class="text-sm">
				Spend is at {{ Math.round(snap.usedRatio * 100) }}% of the tightest day cap
				({{ formatUsdCents(snap.spentTodayCents) }} of {{ formatUsdCents(snap.tightestDayCapCents) }}).
			</p>
		</ZhPanel>
		<p v-else-if="snap" class="text-sm">
			Today {{ formatUsdCents(snap.spentTodayCents) }} /
			{{ formatUsdCents(snap.tightestDayCapCents) }}
			<span class="text-muted-foreground">
				· {{ snap.runsToday }} run{{ snap.runsToday === 1 ? '' : 's' }}
				<template v-if="snap.tightestRunCountDay !== null"> / {{ snap.tightestRunCountDay }} cap</template>
				· max tier {{ snap.tightestMaxModelTier ?? 'none' }}
			</span>
		</p>
		<!-- Narrow the caps table: a tenant capping per task can have dozens of rows here. -->
		<div v-if="snap && snap.caps.length" class="flex flex-wrap items-end gap-3">
			<!-- The test id lands on the field the kit Select mirrors its choice on. -->
			<label class="grid gap-1 text-sm">
				<span class="text-muted-foreground">Layer</span>
				<Select :model-value="layerChoice" data-testid="cap-layer-filter" @update:model-value="chooseLayerFilter">
					<SelectTrigger class="min-w-40">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem :value="ALL_LAYERS">All layers</SelectItem>
						<SelectItem v-for="layer in CAP_LAYERS" :key="layer" :value="layer">{{ layer }}</SelectItem>
					</SelectContent>
				</Select>
			</label>
			<label class="grid gap-1 text-sm">
				<span class="text-muted-foreground">Search</span>
				<ListSearchField
					v-model="capSearch"
					type="search"
					data-testid="cap-search"
					class="min-w-56"
					placeholder="Name or slug"
				/>
			</label>
			<Button v-if="capsFiltered" type="button" size="sm" variant="ghost" @click="clearCapFilters">
				<RotateCcw class="size-4" />
				Clear filters
			</Button>
			<p v-if="capsFiltered" class="text-muted-foreground ml-auto text-sm" data-testid="cap-filter-count">
				{{ visibleCaps.length }} of {{ snap.caps.length }} caps
			</p>
		</div>
		<!-- One row per cap: layer, who it covers, today against that row's own day cap, then its limits. -->
		<ZhPanel v-if="snap" as="div" flush>
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Layer</TableHead>
						<TableHead>Name</TableHead>
						<TableHead>Today / day cap</TableHead>
						<TableHead>Limits</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					<TableRow v-if="!snap.caps.length">
						<TableCell colspan="4" class="text-muted-foreground py-4">
							No caps yet. Add one below. Each cap you add appears here, with today's spend against its
							day limit.
						</TableCell>
					</TableRow>
					<TableRow v-else-if="!visibleCaps.length">
						<TableCell colspan="4" class="text-muted-foreground py-4">No caps match this filter.</TableCell>
					</TableRow>
					<TableRow v-for="cap in visibleCaps" :key="cap.id" :data-row-id="cap.id">
						<TableCell class="text-muted-foreground py-3 align-top">{{ cap.layer }}</TableCell>
						<TableCell class="py-3 align-top font-medium">{{ capLabel(cap.layer, cap.layerId) }}</TableCell>
						<ZhTooltip :text="todaySpendNote(cap)">
							<TableCell
								class="py-3 align-top tabular-nums"
								:class="nearDayCap(cap) ? 'text-destructive font-medium' : ''"
							>
								{{ formatUsdCents(snap.spentTodayCents) }} / {{ formatUsdCents(cap.moneyCentsDay) }}
								<span v-if="dayUsedPercent(cap) !== null" class="text-muted-foreground">({{ dayUsedPercent(cap) }}%)</span>
								<!-- Spend is only recorded enterprise-wide: a non-enterprise row still reads that same total. -->
								<span v-if="cap.layer !== 'enterprise'" class="text-muted-foreground block text-xs">enterprise-wide</span>
							</TableCell>
						</ZhTooltip>
						<TableCell class="py-3 align-top">
							<form
								v-if="drafts[cap.id]"
								class="flex flex-wrap items-end gap-2"
								@submit.prevent="save(cap.layer, cap.layerId, drafts[cap.id], cap.id)"
							>
								<CapLimitFields :ref="capFieldsRef(cap.id)" :draft="drafts[cap.id]" :id-prefix="`cap-${cap.id}`" />
							</form>
						</TableCell>
					</TableRow>
				</TableBody>
			</Table>
		</ZhPanel>
		<!--
			The pickers carry a `name`, so each is a field of this form: inside a form, Reka draws its own hidden
			native select for the choice, which is what makes the target's `required` hold when the form is sent.
		-->
		<!-- #1756: the shared form shell (a section of the Spend page), one help line per field, a preview before saving. -->
		<ZhFormLayout
			title="Add or replace a cap"
			purpose="Sets the spending limits for the whole company or for one team, member, role or task."
			heading="section"
			data-testid="spend-add-form"
			@submit.prevent="openAddPreview"
		>
			<div class="flex flex-wrap items-start gap-3">
				<ZhFormField v-slot="{ controlId, helpId }" label="Layer" help="What the cap covers: the whole company, or one team, member, role or task." example="team">
					<Select :model-value="add.layer" name="layer" data-testid="cap-add-layer" @update:model-value="chooseAddLayer">
						<SelectTrigger :id="controlId" class="min-w-32" :aria-describedby="helpId">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem v-for="layer in CAP_LAYERS" :key="layer" :value="layer">{{ layer }}</SelectItem>
						</SelectContent>
					</Select>
				</ZhFormField>
				<ZhFormField v-if="add.layer === 'enterprise'" v-slot="{ controlId, helpId }" label="Enterprise" help="The company this cap covers, across every team." example="Acme">
					<Input :id="controlId" :model-value="me?.enterprise.name" disabled class="min-w-48" :aria-describedby="helpId" />
				</ZhFormField>
				<!-- #1757's target check (CTO decisions 18 and 20 on #1756): its fixed id, described by its error, then its help line. -->
				<ZhFormField
					v-else
					:id="addFields.fieldId('layerId')"
					:error-id="addFields.message('layerId').id"
					:label="add.layer"
					:help="`The ${add.layer || 'one'} this cap applies to.`"
					example="Engineering"
				>
					<template #default="{ controlId, describedBy }">
						<Select
							:model-value="add.layerId"
							name="layerId"
							required
							data-testid="cap-add-target"
							@update:model-value="chooseAddTarget"
						>
							<SelectTrigger class="min-w-48" v-bind="addFields.bind('layerId')" :id="controlId" :aria-describedby="describedBy">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem v-for="choice in addChoices" :key="choice.id" :value="choice.id">
									{{ choice.label }}
								</SelectItem>
							</SelectContent>
						</Select>
					</template>
					<template #error>
						<ZhFieldError v-bind="addFields.message('layerId')" />
					</template>
				</ZhFormField>
				<CapLimitFields :ref="capFieldsRef('add')" :draft="add" id-prefix="cap-add" helped />
			</div>
			<template #primary>
				<Button type="submit" size="sm">Save</Button>
			</template>
			<template #preview>
				<ZhSavePreview
					v-if="addPreview"
					:lines="addPreview"
					confirm-label="Save"
					:pending="addSaving"
					@confirm="confirmAddPreview"
					@edit="addPreview = null"
				/>
				<p v-else-if="addResult" role="status" class="text-sm" data-testid="spend-add-result">{{ addResult }}</p>
			</template>
		</ZhFormLayout>
		</template>
	</section>
</template>
